import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Briefcase, Plus, Trash2, ArrowLeft, Download, Link2, Sparkles, Globe, Image as ImageIcon, X, PenLine, Save, Eye, Copy, Check } from "lucide-react";
import ThinkingLoader from "@/components/ThinkingLoader";

/**
 * PortfoliosPanel — top-level dashboard view for the Portfolio module.
 * Delegates between the LIST view (all portfolios) and the EDITOR view
 * (single portfolio + items). Uses a lightweight local state machine so
 * the whole feature lives in one file for now; can be split later.
 */
export default function PortfoliosPanel() {
  const { t } = useT();
  const [items, setItems] = useState(null); // null=loading
  const [openId, setOpenId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portfolios");
      setItems(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kon portfolio's niet laden");
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return (
      <PortfolioEditor
        pid={openId}
        onClose={() => { setOpenId(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="portfolios-panel">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="ed-eyebrow inline-flex items-center gap-2">
            <Briefcase size={11} strokeWidth={1.6} /> {t("navPortfolios").toUpperCase()}
          </p>
          <h2 className="font-serif text-3xl leading-tight mt-1">{t("pfTitle")}</h2>
          <p className="text-sm text-neutral-600 max-w-2xl mt-2 leading-relaxed">{t("pfSubtitle")}</p>
        </div>
        <button
          data-testid="portfolio-new-btn"
          onClick={() => setWizardOpen(true)}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 px-4 py-2.5 inline-flex items-center gap-2"
        >
          <Plus size={12} strokeWidth={1.75} /> {t("pfNew")}
        </button>
      </header>

      {items === null && (
        <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500">Laden…</p>
      )}
      {items !== null && items.length === 0 && (
        <div className="border border-dashed border-neutral-300 p-10 text-center" data-testid="portfolios-empty">
          <Briefcase size={22} strokeWidth={1.25} className="mx-auto text-neutral-400 mb-3" />
          <p className="font-serif text-lg text-neutral-800">{t("pfEmpty")}</p>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mt-2 max-w-md mx-auto leading-relaxed">
            {t("pfEmptyHint")}
          </p>
        </div>
      )}
      {items !== null && items.length > 0 && (
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((p, i) => (
            <li
              key={p.id}
              data-testid={`portfolio-card-${i}`}
              className="ed-card p-5 cursor-pointer hover:border-neutral-500 transition-colors relative"
              onClick={() => setOpenId(p.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(p.id); } }}
            >
              {/* Copy public deep-link button — sits next to duplicate.
                  Stops propagation so the row-click doesn't fire. */}
              <button
                type="button"
                data-testid={`portfolio-copy-link-${i}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  const link = `${window.location.origin}/p/${p.user_id || "u"}/${p.slug}`;
                  try {
                    await navigator.clipboard.writeText(link);
                    toast.success(t("pfShareCopied"));
                  } catch {
                    toast.error("Kopiëren mislukt");
                  }
                }}
                className="absolute top-3 right-11 p-1.5 text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                title="Kopieer deelbare link"
              >
                <Link2 size={13} strokeWidth={1.5} />
              </button>
              {/* Duplicate button — sits in the top-right corner, stops
                  propagation so the row-click doesn't also open the editor. */}
              <button
                type="button"
                data-testid={`portfolio-duplicate-${i}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await api.post(`/portfolios/${p.id}/duplicate`);
                    toast.success("Portfolio gedupliceerd");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Dupliceren mislukt");
                  }
                }}
                className="absolute top-3 right-3 p-1.5 text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                title="Dupliceer portfolio"
              >
                <Copy size={13} strokeWidth={1.5} />
              </button>
              <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">/{p.slug}</p>
              <p className="font-serif text-xl mt-1">{p.name}</p>
              {p.tagline && <p className="text-sm text-neutral-600 mt-1">{p.tagline}</p>}
              <div className="flex items-center justify-between mt-3 gap-3">
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500">
                  {new Date(p.created_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}
                </p>
                {p.views > 0 && (
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-700 inline-flex items-center gap-1" data-testid={`portfolio-views-${i}`}>
                    <Eye size={11} strokeWidth={1.5} /> {p.views}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {wizardOpen && (
        <PortfolioWizard
          onCreated={(p) => { setWizardOpen(false); setOpenId(p.id); load(); }}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wizard — create a new portfolio (name + tagline).
// ---------------------------------------------------------------------------
function PortfolioWizard({ onCreated, onClose }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [template, setTemplate] = useState("creative");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/portfolios", {
        name: name.trim(),
        tagline: tagline.trim(),
        template,
      });
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  };

  const templates = [
    { key: "creative",  label: "Creative",  hint: "Zwart + amber + kleuraccenten (default)" },
    { key: "corporate", label: "Corporate", hint: "Navy blauw, strak, formal" },
    { key: "minimal",   label: "Minimal",   hint: "Alleen ink + witruimte" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="portfolio-wizard-modal"
        className="bg-white max-w-lg w-full shadow-2xl border border-neutral-200 p-6 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-2xl leading-tight">{t("pfWizardTitle")}</h3>
          <button type="button" onClick={onClose} aria-label="close" className="text-neutral-500 hover:text-neutral-900">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <div>
          <label className="ed-label">{t("pfWizardNameLabel")}</label>
          <input
            data-testid="portfolio-wizard-name"
            className="ed-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("pfWizardNamePh")}
            autoFocus
          />
        </div>
        <div>
          <label className="ed-label">{t("pfWizardTaglineLabel")}</label>
          <input
            data-testid="portfolio-wizard-tagline"
            className="ed-input"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder={t("pfWizardTaglinePh")}
          />
        </div>
        <div>
          <label className="ed-label">Template</label>
          <div className="grid grid-cols-3 gap-2 mt-1" data-testid="portfolio-wizard-templates">
            {templates.map((tpl) => (
              <button
                key={tpl.key}
                type="button"
                data-testid={`portfolio-wizard-template-${tpl.key}`}
                onClick={() => setTemplate(tpl.key)}
                className={`text-left p-2 border transition-colors ${
                  template === tpl.key
                    ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
                    : "border-neutral-300 hover:border-neutral-900"
                }`}
              >
                {/* Live PDF-preview thumbnail so users can compare the
                    three templates before committing to one. Served by
                    the backend so any style change is auto-reflected. */}
                <div className="w-full aspect-[210/297] bg-neutral-100 mb-2 overflow-hidden border border-neutral-200">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/portfolios/template-preview/${tpl.key}.png`}
                    alt={`${tpl.label} preview`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em]">{tpl.label}</p>
                <p className={`text-[0.6rem] leading-relaxed mt-1 ${template === tpl.key ? "text-neutral-300" : "text-neutral-500"}`}>{tpl.hint}</p>
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          data-testid="portfolio-wizard-submit"
          disabled={saving || !name.trim()}
          className="w-full font-mono text-[0.7rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50 py-3"
        >
          {saving ? "…" : t("pfCreate")}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor — single portfolio with its items.
// ---------------------------------------------------------------------------
function PortfolioEditor({ pid, onClose }) {
  const { t } = useT();
  const [state, setState] = useState({ portfolio: null, items: [] });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  // CV-skill options for the per-project multi-select. Loaded once per
  // editor session — the CV rarely changes mid-flight, and the endpoint
  // is a cheap lookup on the default resume.
  const [skillOptions, setSkillOptions] = useState([]);
  const [skillHasCv, setSkillHasCv] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/portfolios/skill-suggestions");
        if (!cancelled) {
          setSkillOptions(Array.isArray(data?.skills) ? data.skills : []);
          setSkillHasCv(data?.has_cv !== false);
        }
      } catch {
        if (!cancelled) setSkillOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/portfolios/${pid}`);
      setState({ portfolio: data.portfolio, items: data.items || [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kon portfolio niet laden");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [pid, onClose]);
  useEffect(() => { load(); }, [load]);

  const shareUrl = state.portfolio
    ? `${window.location.origin}/p/${state.portfolio.user_id || "u"}/${state.portfolio.slug}`
    : "";

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("pfShareCopied"));
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const [pdfLang, setPdfLang] = useState("nl");

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/portfolios/${pid}/pdf`, {
        params: { lang: pdfLang },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `portfolio-${state.portfolio.slug}-${pdfLang}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF download mislukt");
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm(t("pfDeleteConfirm"))) return;
    try {
      await api.delete(`/portfolios/${pid}/items/${itemId}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const removePortfolio = async () => {
    if (!window.confirm(t("pfDeletePortfolioConfirm"))) return;
    try {
      await api.delete(`/portfolios/${pid}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  if (loading || !state.portfolio) {
    return <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500">Laden…</p>;
  }

  return (
    <div className="space-y-6" data-testid="portfolio-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            data-testid="portfolio-editor-back"
            onClick={onClose}
            className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1.5 mb-2"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> {t("pfEditorBack")}
          </button>
          <h2 className="font-serif text-3xl leading-tight">{state.portfolio.name}</h2>
          {state.portfolio.tagline && <p className="text-sm text-neutral-600 mt-1">{state.portfolio.tagline}</p>}
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mt-2 inline-flex flex-wrap items-center gap-3">
            <span>{t("pfItemsCount", { n: state.items.length })}</span>
            {state.portfolio.views > 0 && (
              <span data-testid="portfolio-editor-views" className="inline-flex items-center gap-1 text-neutral-700">
                <Eye size={11} strokeWidth={1.5} /> {state.portfolio.views} views
                {state.portfolio.last_viewed_at && (
                  <span className="text-neutral-500 normal-case ml-1">
                    · laatst {new Date(state.portfolio.last_viewed_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}
                  </span>
                )}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="portfolio-share-copy"
            onClick={copyShare}
            className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-3 py-2 inline-flex items-center gap-1.5"
          >
            <Link2 size={12} strokeWidth={1.75} /> {t("pfShareLink")}
          </button>
          <div
            data-testid="portfolio-pdf-lang-toggle"
            className="inline-flex items-stretch border border-neutral-300"
            role="group"
            aria-label="PDF taal"
          >
            {[
              { code: "nl", label: "NL" },
              { code: "en", label: "EN" },
            ].map(({ code, label }) => (
              <button
                key={code}
                type="button"
                onClick={() => setPdfLang(code)}
                data-testid={`portfolio-pdf-lang-${code}`}
                aria-pressed={pdfLang === code}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] px-2.5 py-2 transition-colors ${
                  pdfLang === code
                    ? "bg-neutral-900 text-[#F9F8F6]"
                    : "bg-transparent text-neutral-500 hover:text-neutral-900"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            data-testid="portfolio-pdf-download"
            onClick={downloadPdf}
            className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 px-3 py-2 inline-flex items-center gap-1.5"
          >
            <Download size={12} strokeWidth={1.75} /> {t("pfDownloadPdf")}
          </button>
        </div>
      </div>

      <SlugEditor
        pid={pid}
        currentSlug={state.portfolio.slug}
        shareUrl={shareUrl}
        onCopy={copyShare}
        onChanged={load}
      />

      <TemplateSwitcher
        pid={pid}
        current={state.portfolio.template || "creative"}
        name={state.portfolio.name}
        tagline={state.portfolio.tagline || ""}
        onChanged={load}
      />

      <CoverPanel
        pid={pid}
        portfolio={state.portfolio}
        itemCount={state.items.length}
        hasImages={state.items.some((it) => (it.image_paths || []).length > 0)}
        onChanged={load}
      />

      <div className="space-y-3">
        {state.items.map((it, i) => (
          <ItemCard
            key={it.id}
            pid={pid}
            item={it}
            index={i}
            skillOptions={skillOptions}
            skillHasCv={skillHasCv}
            editing={editingId === it.id}
            onEdit={() => setEditingId(it.id)}
            onCancel={() => setEditingId(null)}
            onSaved={() => { setEditingId(null); load(); }}
            onDelete={() => removeItem(it.id)}
            // HTML5 DnD hooks — same pattern as References + Skills.
            onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            onDrop={async (e) => {
              e.preventDefault();
              const from = parseInt(e.dataTransfer.getData("text/plain"), 10);
              if (Number.isNaN(from) || from === i) return;
              const next = [...state.items];
              const [moved] = next.splice(from, 1);
              next.splice(i, 0, moved);
              setState((s) => ({ ...s, items: next }));
              try {
                await api.put(`/portfolios/${pid}/items/reorder`, { item_ids: next.map((x) => x.id) });
              } catch {
                toast.error("Volgorde opslaan mislukt");
                load();
              }
            }}
          />
        ))}
        {adding && (
          <ItemCard pid={pid} item={null} index={state.items.length}
            skillOptions={skillOptions}
            skillHasCv={skillHasCv}
            editing={true}
            onEdit={() => {}}
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); load(); }}
            onDelete={() => setAdding(false)}
          />
        )}
        {!adding && (
          <button
            type="button"
            data-testid="portfolio-add-item"
            onClick={() => setAdding(true)}
            className="w-full border border-dashed border-neutral-300 hover:border-neutral-500 text-neutral-600 hover:text-neutral-900 py-6 font-mono text-[0.65rem] uppercase tracking-[0.15em]"
          >
            {t("pfAddItem")}
          </button>
        )}
      </div>

      <div className="pt-4 border-t border-neutral-100">
        <button
          data-testid="portfolio-delete"
          onClick={removePortfolio}
          className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-rose-600 hover:text-rose-800 inline-flex items-center gap-1.5"
        >
          <Trash2 size={12} strokeWidth={1.5} /> {t("pfDeletePortfolio")}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skills multi-select — pulls options from the user's default CV
// (`/portfolios/skill-suggestions`) and lets the user toggle which
// ones are relevant for this specific project. Chips render below
// the responsibilities editor. Cap of 25 mirrors backend.
// ---------------------------------------------------------------------------
function SkillsPicker({ options, selected, onChange, hasCv, t, projectName, description, responsibilities, websiteUrl }) {
  const MAX_SKILLS = 25;
  const [query, setQuery] = useState("");
  // AI-suggested skills — highlighted as a preview row above the CV
  // picker so the user can accept them individually or all-at-once
  // without losing sight of their manual picks.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggested, setAiSuggested] = useState([]);
  const selectedSet = new Set(selected);

  const toggle = (name) => {
    if (selectedSet.has(name)) {
      onChange(selected.filter((x) => x !== name));
      return;
    }
    if (selected.length >= MAX_SKILLS) {
      toast.error(t("pfItemSkillsLimitReached"));
      return;
    }
    onChange([...selected, name]);
  };

  const canSuggest = ((description || "") + (responsibilities || "")).trim().length >= 10;

  const runSuggest = async () => {
    if (!canSuggest) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/portfolios/suggest-skills", {
        project_name: projectName || "",
        description: description || "",
        responsibilities: responsibilities || "",
        website_url: websiteUrl || "",
        current: selected,
      });
      const raw = Array.isArray(data?.suggested) ? data.suggested : [];
      if (raw.length === 0) {
        toast.info(t("pfItemSkillsAiEmpty"));
        setAiSuggested([]);
        return;
      }
      setAiSuggested(raw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pfItemSkillsAiFail"));
    } finally {
      setAiBusy(false);
    }
  };

  const acceptAll = () => {
    // Pull suggestions in one shot, respecting the cap. Skip anything
    // already selected (shouldn't happen — the backend filters `current` —
    // but defensive coding beats a UX surprise).
    const room = Math.max(0, MAX_SKILLS - selected.length);
    if (room === 0) {
      toast.error(t("pfItemSkillsLimitReached"));
      return;
    }
    const add = aiSuggested
      .filter((s) => !selectedSet.has(s))
      .slice(0, room);
    if (add.length === 0) return;
    onChange([...selected, ...add]);
    // Keep the preview visible but remove accepted items so the row
    // shrinks to whatever the user still hasn't picked.
    setAiSuggested((prev) => prev.filter((s) => !add.includes(s)));
    toast.success(t("pfItemSkillsAiAdded").replace("{n}", add.length));
  };

  const acceptOne = (name) => {
    if (selectedSet.has(name)) return;
    if (selected.length >= MAX_SKILLS) {
      toast.error(t("pfItemSkillsLimitReached"));
      return;
    }
    onChange([...selected, name]);
    setAiSuggested((prev) => prev.filter((s) => s !== name));
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((n) => n.toLowerCase().includes(q))
    : options;

  return (
    <fieldset
      className="border border-neutral-200 bg-white p-3 space-y-2"
      data-testid="portfolio-item-skills-group"
    >
      <legend className="px-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
        {t("pfItemSkillsLabel")}
      </legend>
      {!hasCv || options.length === 0 ? (
        <p className="text-xs text-neutral-500 italic">
          {t("pfItemSkillsNoCv")}
        </p>
      ) : (
        <>
          <p className="text-xs text-neutral-500">
            {t("pfItemSkillsHelp")}
          </p>
          {options.length > 8 && (
            <input
              type="text"
              data-testid="portfolio-item-skills-search"
              className="ed-input"
              placeholder={t("pfItemSkillsSearchPh")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <div
            className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1"
            data-testid="portfolio-item-skills-options"
          >
            {filtered.map((name) => {
              const active = selectedSet.has(name);
              return (
                <button
                  key={name}
                  type="button"
                  data-testid={`portfolio-item-skill-opt-${name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                  onClick={() => toggle(name)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-[0.72rem] leading-tight border transition-colors ${
                    active
                      ? "bg-neutral-900 border-neutral-900 text-[#F9F8F6]"
                      : "border-neutral-300 text-neutral-700 hover:border-neutral-900"
                  }`}
                >
                  {active && <Check size={11} strokeWidth={2} />}
                  {name}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <span className="text-xs text-neutral-500 italic">{t("pfItemSkillsNoMatch")}</span>
            )}
          </div>
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
            {selected.length} / {MAX_SKILLS}
          </p>

          {/* AI suggest row — pulls CV-skills that match the project
              text. Kept visually separate from the manual chip-picker so
              the AI's picks feel like a suggestion, not an override. */}
          <div className="pt-2 border-t border-neutral-200 space-y-2" data-testid="portfolio-item-skills-ai-row">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                data-testid="portfolio-item-skills-ai-btn"
                onClick={runSuggest}
                disabled={aiBusy || !canSuggest}
                title={canSuggest ? "" : t("pfItemSkillsAiNeedText")}
                className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 inline-flex items-center gap-1.5"
              >
                <Sparkles size={12} strokeWidth={1.75} /> {aiBusy ? t("pfItemSkillsAiBusy") : t("pfItemSkillsAiCta")}
              </button>
              {aiBusy && <ThinkingLoader stage="letter" testId="portfolio-skills-ai-thinking" />}
              {aiSuggested.length > 0 && (
                <button
                  type="button"
                  data-testid="portfolio-item-skills-ai-accept-all"
                  onClick={acceptAll}
                  className="font-mono text-[0.65rem] uppercase tracking-[0.15em] bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-800 px-3 py-2 inline-flex items-center gap-1.5"
                >
                  <Check size={12} strokeWidth={2} /> {t("pfItemSkillsAiAcceptAll").replace("{n}", aiSuggested.length)}
                </button>
              )}
            </div>
            {aiSuggested.length > 0 && (
              <>
                <p className="text-[0.7rem] text-amber-800 bg-amber-50 border-l-2 border-amber-500 px-2 py-1">
                  {t("pfItemSkillsAiHint")}
                </p>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="portfolio-item-skills-ai-suggestions"
                >
                  {aiSuggested.map((name) => (
                    <button
                      key={`ai-${name}`}
                      type="button"
                      data-testid={`portfolio-item-skill-ai-opt-${name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                      onClick={() => acceptOne(name)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[0.72rem] leading-tight border border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-500 hover:text-neutral-900 transition-colors"
                    >
                      <Plus size={11} strokeWidth={2} />
                      {name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Item card — read view + inline edit form.
// ---------------------------------------------------------------------------
function ItemCard({ pid, item, index, skillOptions = [], skillHasCv = true, editing, onEdit, onCancel, onSaved, onDelete, onDragStart, onDragOver, onDrop }) {
  const { t } = useT();
  const [projectName, setProjectName] = useState(item?.project_name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [responsibilities, setResponsibilities] = useState(item?.responsibilities || "");
  const [websiteUrl, setWebsiteUrl] = useState(item?.website_url || "");
  const [imagePaths, setImagePaths] = useState(item?.image_paths || []);
  const [selectedSkills, setSelectedSkills] = useState(Array.isArray(item?.skills) ? item.skills : []);
  const [aiBusy, setAiBusy] = useState(false);
  const [respAiBusy, setRespAiBusy] = useState(false);
  const [crawlBusy, setCrawlBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  // Crawl-picker state — set after a successful `/crawl` so the user can
  // choose which images to save to the portfolio item. `screenshot_path`
  // already lives in our bucket (auto-uploaded during the crawl), so we
  // can drop it straight into `imagePaths`. External URLs need a POST to
  // `/save-remote-image` first so we copy them into our own storage.
  const [crawlResult, setCrawlResult] = useState(null);
  const [pickBusy, setPickBusy] = useState(false);
  const fileRef = useRef(null);

  const runAi = async () => {
    if (!description.trim() || description.trim().length < 10) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/portfolios/rewrite", {
        project_name: projectName,
        description,
      });
      if (data?.description) setDescription(data.description);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI herschrijven mislukt");
    } finally {
      setAiBusy(false);
    }
  };

  const runRespAi = async () => {
    if (!responsibilities.trim() || responsibilities.trim().length < 10) return;
    setRespAiBusy(true);
    try {
      const { data } = await api.post("/portfolios/rewrite", {
        project_name: projectName,
        description: responsibilities,
        kind: "responsibilities",
      });
      if (data?.description) setResponsibilities(data.description);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI herschrijven mislukt");
    } finally {
      setRespAiBusy(false);
    }
  };

  const runCrawl = async () => {
    if (!websiteUrl.trim()) return;
    setCrawlBusy(true);
    try {
      const { data } = await api.post("/portfolios/crawl", { url: websiteUrl });
      const snippet = data.description || data.text || "";
      if (snippet) setDescription((prev) => prev.trim() ? prev : snippet.slice(0, 800));
      if (data.title && !projectName.trim()) setProjectName(data.title);
      // Show the image picker so the user can decide which images to keep.
      setCrawlResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Website ophalen mislukt");
    } finally {
      setCrawlBusy(false);
    }
  };

  const useScreenshot = () => {
    if (!crawlResult?.screenshot_path) return;
    setImagePaths((prev) => {
      if (prev.includes(crawlResult.screenshot_path)) return prev;
      return [...prev, crawlResult.screenshot_path].slice(0, 6);
    });
    toast.success("Screenshot toegevoegd");
  };

  const pickCandidateImage = async (imgUrl) => {
    if (imagePaths.length >= 6) {
      toast.error("Max 6 afbeeldingen per project");
      return;
    }
    setPickBusy(true);
    try {
      const { data } = await api.post(`/portfolios/${pid}/save-remote-image`, { url: imgUrl });
      if (data?.path) {
        setImagePaths((prev) => [...prev, data.path].slice(0, 6));
        toast.success("Afbeelding toegevoegd");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Afbeelding opslaan mislukt");
    } finally {
      setPickBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploadBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/portfolios/${pid}/upload-image`, fd);
      if (data?.path) setImagePaths((prev) => [...prev, data.path].slice(0, 6));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload mislukt");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!projectName.trim()) {
      toast.error("Projectnaam is verplicht");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_name: projectName,
        description,
        responsibilities,
        website_url: websiteUrl,
        image_paths: imagePaths,
        skills: selectedSkills,
      };
      if (item?.id) {
        await api.put(`/portfolios/${pid}/items/${item.id}`, payload);
      } else {
        await api.post(`/portfolios/${pid}/items`, payload);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div
        className="ed-card p-5 space-y-2"
        data-testid={`portfolio-item-${index}`}
        draggable={!!onDragStart}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Leading cover thumbnail — the first image is what lands on
              the CV sluitstuk hero, so we surface it here with a
              "COVER" ribbon so the user knows exactly which image will
              appear where. */}
          {(item.image_paths || []).length > 0 && (
            <div className="relative shrink-0" data-testid={`portfolio-item-cover-thumb-${index}`}>
              <PortfolioImage path={item.image_paths[0]} />
              <span className="absolute top-1 left-1 font-mono text-[0.5rem] uppercase tracking-[0.12em] bg-amber-500 text-neutral-900 px-1 py-0.5 leading-none">
                Cover
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 inline-flex items-center gap-2">
              <span className="opacity-60 cursor-grab active:cursor-grabbing select-none" title="Sleep om te sorteren">⋮⋮</span>
              Project {index + 1}
            </p>
            <p className="font-serif text-xl mt-0.5">{item.project_name}</p>
            {item.website_url && (() => {
              // Try to display the hostname; if the stored URL is malformed
              // (missing protocol / typo) fall back to the raw string so
              // the entire editor never crashes with "Failed to construct 'URL'".
              const abs = /^[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(item.website_url)
                ? item.website_url
                : `https://${item.website_url.replace(/^\/+/, "")}`;
              let host = item.website_url;
              try { host = new URL(abs).hostname.replace(/^www\./, ""); } catch { /* keep raw */ }
              return (
                <a href={abs} target="_blank" rel="noreferrer" className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mt-1">
                  <Globe size={11} strokeWidth={1.5} /> {host}
                </a>
              );
            })()}
          </div>
          <div className="flex items-center gap-1">
            {/* Cover-source toggle — quick opt-out for weaker screenshots
                so they don't muddy the AI cover moodboard. Defaults to
                true (legacy items stay in). */}
            <label
              className="inline-flex items-center gap-1.5 cursor-pointer px-2 py-1 hover:bg-neutral-50 select-none"
              title="Gebruik de eerste afbeelding van dit project als bron voor de AI cover"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                data-testid={`portfolio-item-use-for-cover-${index}`}
                defaultChecked={item.use_for_cover !== false}
                onChange={async (e) => {
                  const next = e.target.checked;
                  try {
                    await api.patch(`/portfolios/${pid}/items/${item.id}/use-for-cover`, { use_for_cover: next });
                    toast.success(next ? "Meegenomen in AI cover" : "Uitgesloten van AI cover");
                  } catch (err) {
                    e.target.checked = !next; // revert on failure
                    toast.error(err?.response?.data?.detail || "Kon niet bijwerken");
                  }
                }}
                className="w-3.5 h-3.5 accent-amber-600"
              />
              <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-500">Voor cover</span>
            </label>
            <button data-testid={`portfolio-item-edit-${index}`} onClick={onEdit} className="p-2 text-neutral-500 hover:text-neutral-900" title={t("pfEditItem")}>
              <PenLine size={13} strokeWidth={1.5} />
            </button>
            <button data-testid={`portfolio-item-delete-${index}`} onClick={onDelete} className="p-2 text-rose-500 hover:text-rose-700" title={t("pfDeleteItem")}>
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {item.description && <p className="text-sm text-neutral-700 leading-relaxed">{item.description}</p>}
        {item.responsibilities && (
          <div className="pt-1" data-testid={`portfolio-item-resp-${index}`}>
            <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 mb-1">
              {t("pfItemRespLabel")}
            </p>
            <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{item.responsibilities}</p>
          </div>
        )}
        {(item.skills || []).length > 0 && (
          <div className="pt-2" data-testid={`portfolio-item-skills-${index}`}>
            <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 mb-1.5">
              {t("pfItemSkillsLabel")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {item.skills.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center border border-neutral-300 text-neutral-700 px-2 py-0.5 text-[0.7rem] leading-tight"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
        {item.image_paths && item.image_paths.length > 1 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {item.image_paths.slice(1).map((p) => (
              <PortfolioImage key={p} path={p} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ed-card p-5 space-y-3 border-neutral-900" data-testid={`portfolio-item-editor-${index}`}>
      <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">
        {item ? `Bewerken · Project ${index + 1}` : `Nieuw project`}
      </p>
      <input
        data-testid="portfolio-item-name-input"
        className="ed-input"
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        placeholder={t("pfItemNamePh")}
      />
      <div className="flex gap-2">
        <input
          data-testid="portfolio-item-url-input"
          className="ed-input flex-1"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder={t("pfItemUrlPh")}
        />
        <button
          type="button"
          data-testid="portfolio-item-crawl-btn"
          onClick={runCrawl}
          disabled={crawlBusy || !websiteUrl.trim()}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 disabled:opacity-50 px-3 py-2 inline-flex items-center gap-1.5 shrink-0"
        >
          <Globe size={12} strokeWidth={1.75} /> {crawlBusy ? t("pfCrawlBusy") : t("pfCrawlHint")}
        </button>
      </div>
      {/* Beschrijving — visually grouped in a bordered block with a clear
          header + its own AI button, so it can't be confused with the
          Verantwoordelijkheden block below. */}
      <fieldset
        className="border border-neutral-200 bg-white p-3 space-y-2"
        data-testid="portfolio-item-desc-group"
      >
        <legend className="px-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
          {t("pfItemDescLabel")}
        </legend>
        <textarea
          data-testid="portfolio-item-desc-input"
          className="ed-input"
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("pfItemDescPh")}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="portfolio-item-ai-btn"
            onClick={runAi}
            disabled={aiBusy || description.trim().length < 10}
            className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 disabled:opacity-50 px-3 py-2 inline-flex items-center gap-1.5"
          >
            <Sparkles size={12} strokeWidth={1.75} /> {aiBusy ? t("pfAiRewriting") : t("pfAiRewriteDesc")}
          </button>
          {aiBusy && <ThinkingLoader stage="letter" testId="portfolio-ai-thinking" />}
        </div>
      </fieldset>
      {/* Verantwoordelijkheden — separate bordered block with its own
          header + AI button so users know exactly which section the
          "AI verantwoordelijkheden" button rewrites. Uses a different
          prompt server-side (action-verb bullets). */}
      <fieldset
        className="border border-neutral-200 bg-white p-3 space-y-2"
        data-testid="portfolio-item-resp-group"
      >
        <legend className="px-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
          {t("pfItemRespLabel")}
        </legend>
        <textarea
          data-testid="portfolio-item-resp-input"
          className="ed-input"
          rows={5}
          value={responsibilities}
          onChange={(e) => setResponsibilities(e.target.value)}
          placeholder={t("pfItemRespPh")}
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="portfolio-item-resp-ai-btn"
            onClick={runRespAi}
            disabled={respAiBusy || responsibilities.trim().length < 10}
            className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 disabled:opacity-50 px-3 py-2 inline-flex items-center gap-1.5"
          >
            <Sparkles size={12} strokeWidth={1.75} /> {respAiBusy ? t("pfAiRewriting") : t("pfAiRewriteResp")}
          </button>
          {respAiBusy && <ThinkingLoader stage="letter" testId="portfolio-resp-ai-thinking" />}
        </div>
      </fieldset>

      <SkillsPicker
        options={skillOptions}
        selected={selectedSkills}
        onChange={setSelectedSkills}
        hasCv={skillHasCv}
        t={t}
        projectName={projectName}
        description={description}
        responsibilities={responsibilities}
        websiteUrl={websiteUrl}
      />
      <div className="flex items-center gap-2 flex-wrap pt-2">
        <input
          ref={fileRef}
          data-testid="portfolio-item-image-input"
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
        />
        <button
          type="button"
          data-testid="portfolio-item-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploadBusy || imagePaths.length >= 6}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 disabled:opacity-50 px-3 py-2 inline-flex items-center gap-1.5"
        >
          <ImageIcon size={12} strokeWidth={1.75} /> {uploadBusy ? t("pfImageUploading") : t("pfImageUpload")}
        </button>
      </div>
      {imagePaths.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {imagePaths.map((p) => (
            <div key={p} className="relative group">
              <PortfolioImage path={p} />
              <button
                type="button"
                onClick={() => setImagePaths((prev) => prev.filter((x) => x !== p))}
                className="absolute -top-2 -right-2 bg-rose-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100"
                aria-label="verwijder"
              >
                <X size={11} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}
      {crawlResult && (
        <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-2" data-testid="portfolio-crawl-picker">
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600">
              Gevonden op site — kies wat je wilt gebruiken
            </p>
            <button
              type="button"
              onClick={() => setCrawlResult(null)}
              className="text-neutral-500 hover:text-neutral-900"
              aria-label="sluit picker"
            >
              <X size={12} strokeWidth={1.5} />
            </button>
          </div>
          {crawlResult.screenshot_path && (
            <div className="flex items-center gap-3 border border-neutral-200 bg-white p-2">
              <PortfolioImage path={crawlResult.screenshot_path} />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-sm">Screenshot homepage</p>
                <p className="text-xs text-neutral-500 truncate">Auto-gegenereerd op basis van {crawlResult.url}</p>
              </div>
              <button
                type="button"
                data-testid="portfolio-crawl-use-screenshot"
                onClick={useScreenshot}
                disabled={imagePaths.includes(crawlResult.screenshot_path)}
                className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50 px-3 py-1.5 shrink-0"
              >
                {imagePaths.includes(crawlResult.screenshot_path) ? "Toegevoegd" : "Gebruik"}
              </button>
            </div>
          )}
          {(crawlResult.candidate_images || []).length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="portfolio-crawl-images">
              {crawlResult.candidate_images.map((imgUrl, i) => (
                <button
                  key={imgUrl}
                  type="button"
                  data-testid={`portfolio-crawl-image-${i}`}
                  onClick={() => pickCandidateImage(imgUrl)}
                  disabled={pickBusy}
                  title="Voeg toe aan project"
                  className="relative aspect-square border border-neutral-200 bg-white hover:border-neutral-900 transition-colors overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img
                    src={imgUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center font-mono text-[0.6rem] uppercase tracking-[0.15em] text-white transition-opacity">
                    + Toevoegen
                  </span>
                </button>
              ))}
            </div>
          )}
          {!crawlResult.screenshot_path && (crawlResult.candidate_images || []).length === 0 && (
            <p className="text-xs text-neutral-500 italic">Geen bruikbare afbeeldingen gevonden op deze pagina.</p>
          )}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 px-3 py-2"
        >
          Annuleren
        </button>
        <button
          type="button"
          data-testid="portfolio-item-save-btn"
          onClick={save}
          disabled={saving || !projectName.trim()}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50 px-3 py-2 inline-flex items-center gap-1.5"
        >
          <Save size={12} strokeWidth={1.75} /> {saving ? "…" : t("pfSaveItem")}
        </button>
      </div>
    </div>
  );
}

/**
 * Authenticated image — fetches the blob through the backend so the
 * Emergent bucket auth stays server-side. Revokes the blob URL on
 * unmount to prevent memory leaks.
 */
function PortfolioImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        const res = await api.get(`/portfolios/image`, {
          params: { path },
          responseType: "blob",
        });
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {
        /* image is best-effort; skip on failure */
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  return (
    <div className="w-24 h-24 border border-neutral-200 bg-neutral-100 overflow-hidden">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : null}
    </div>
  );
}


/**
 * SlugEditor — inline editor for the portfolio's vanity slug.
 */
function SlugEditor({ pid, currentSlug, shareUrl, onCopy, onChanged }) {
  const { t } = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentSlug || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(currentSlug || ""); }, [currentSlug]);

  const normalized = draft.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  const dirty = normalized !== (currentSlug || "");
  const canSave = normalized.length >= 2 && dirty && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.put(`/portfolios/${pid}/slug`, { slug: normalized });
      toast.success("Slug bijgewerkt");
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Slug opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="border border-neutral-200 bg-neutral-50 p-3 flex flex-wrap items-center justify-between gap-3" data-testid="portfolio-slug-bar">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-600">{t("pfPublicHint")}</p>
          <p className="font-mono text-[0.65rem] text-neutral-900 break-all mt-1">{shareUrl}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            data-testid="portfolio-slug-edit"
            onClick={() => setEditing(true)}
            className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <PenLine size={11} strokeWidth={1.5} /> Slug
          </button>
          <button
            type="button"
            data-testid="portfolio-slug-copy"
            onClick={onCopy}
            className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-3 py-1.5 inline-flex items-center gap-1.5"
          >
            <Copy size={11} strokeWidth={1.5} /> Kopieer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-neutral-900 p-3 space-y-2" data-testid="portfolio-slug-editor">
      <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-700">Aangepaste slug voor de deelbare link</p>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-neutral-500 whitespace-nowrap">…/p/&hellip;/</span>
        <input
          data-testid="portfolio-slug-input"
          className="ed-input flex-1"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="design-2026"
          autoFocus
        />
      </div>
      <p className="text-[0.65rem] font-mono text-neutral-500">
        Alleen a-z, 0-9 en `-`. Preview: <span className="text-neutral-900">…/p/&hellip;/{normalized || "?"}</span>
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="portfolio-slug-cancel"
          onClick={() => { setEditing(false); setDraft(currentSlug || ""); }}
          className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 px-3 py-1.5"
        >
          Annuleren
        </button>
        <button
          type="button"
          data-testid="portfolio-slug-save"
          onClick={save}
          disabled={!canSave}
          className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-50 px-3 py-1.5 inline-flex items-center gap-1.5"
        >
          <Check size={11} strokeWidth={2} /> {saving ? "…" : "Opslaan"}
        </button>
      </div>
    </div>
  );
}

/**
 * TemplateSwitcher — inline template picker for a portfolio. Persists via
 * `PUT /portfolios/{pid}` (the shared update endpoint that also owns
 * name/tagline). Fires `onChanged()` so the editor + PDF preview refresh
 * with the new palette.
 */
function TemplateSwitcher({ pid, current, name, tagline, onChanged }) {
  const [saving, setSaving] = useState(false);
  const templates = [
    { key: "creative",  label: "Creative",  swatch: ["#0F0F10", "#D9A441"] },
    { key: "corporate", label: "Corporate", swatch: ["#1F2A44", "#5B7BA6"] },
    { key: "minimal",   label: "Minimal",   swatch: ["#FAF7F1", "#1A1918"] },
  ];
  const switchTo = async (key) => {
    if (key === current || saving) return;
    setSaving(true);
    try {
      await api.put(`/portfolios/${pid}`, { name, tagline, template: key });
      toast.success("Template geüpdatet");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Template opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border border-neutral-200 bg-white p-3 flex flex-wrap items-center gap-3" data-testid="portfolio-template-switcher">
      <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600 shrink-0">Template</p>
      <div className="flex flex-wrap gap-2 flex-1">
        {templates.map((tpl) => (
          <button
            key={tpl.key}
            type="button"
            data-testid={`portfolio-template-${tpl.key}`}
            onClick={() => switchTo(tpl.key)}
            disabled={saving}
            className={`inline-flex items-center gap-2 border px-3 py-1.5 transition-colors disabled:opacity-50 ${
              current === tpl.key ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]" : "border-neutral-300 hover:border-neutral-900"
            }`}
          >
            <span className="flex gap-0.5">
              {tpl.swatch.map((c) => (
                <span key={c} className="w-3 h-3 border border-neutral-400" style={{ backgroundColor: c }} />
              ))}
            </span>
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em]">{tpl.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}



/**
 * CoverPanel — Generate → Preview → Confirm flow for the AI hero cover.
 *
 * `POST /portfolios/{pid}/generate-cover` now writes to `cover_preview_*`
 * fields, NOT to the committed `cover_image_*`. The user then either:
 *   - "Bevestig"  → POST `commit-cover`, promoting preview → cover.
 *   - "Opnieuw"   → POST `generate-cover` again with the same hint.
 *                   Consumes credits per attempt (each hit Gemini).
 *   - "Verwerpen" → DELETE `cover-preview` (blob + fields discarded).
 * When no preview is pending, the panel falls back to showing the
 * currently-committed cover with a plain "Ververs" / "Verwijderen".
 */
function CoverPanel({ pid, portfolio, itemCount, hasImages, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [committedSrc, setCommittedSrc] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const initialHint = portfolio?.cover_preview_style_hint || portfolio?.cover_style_hint || "";
  const [styleHint, setStyleHint] = useState(initialHint);
  const committedPath = portfolio?.cover_image_path;
  const previewPath = portfolio?.cover_preview_path;

  // Keep the input in sync when the portfolio reloads. Prefer the
  // in-flight preview hint over the committed one so the user sees what
  // was just tried.
  useEffect(() => {
    setStyleHint(portfolio?.cover_preview_style_hint || portfolio?.cover_style_hint || "");
  }, [portfolio?.cover_style_hint, portfolio?.cover_preview_style_hint]);

  // Fetch an authenticated blob → object URL for both the committed
  // cover and the pending preview independently, so switching between
  // them (or seeing them side-by-side) doesn't refetch unnecessarily.
  useEffect(() => {
    if (!committedPath) { setCommittedSrc(null); return; }
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        const res = await api.get(`/portfolios/image`, { params: { path: committedPath }, responseType: "blob" });
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setCommittedSrc(objectUrl);
      } catch { /* silent */ }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [committedPath]);

  useEffect(() => {
    if (!previewPath) { setPreviewSrc(null); return; }
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        const res = await api.get(`/portfolios/image`, { params: { path: previewPath }, responseType: "blob" });
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setPreviewSrc(objectUrl);
      } catch { /* silent */ }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [previewPath]);

  const canGenerate = itemCount > 0 && hasImages;
  const hasPreview = Boolean(previewPath);

  const runGenerate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/portfolios/${pid}/generate-cover`, { style_hint: styleHint.trim() });
      toast.success(hasPreview ? "Nieuwe preview gegenereerd" : "Preview klaar — bekijk en bevestig");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI cover mislukt");
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/portfolios/${pid}/commit-cover`);
      toast.success("Cover bevestigd en zichtbaar op de publieke pagina");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bevestigen mislukt");
    } finally {
      setBusy(false);
    }
  };

  const runDiscardPreview = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/portfolios/${pid}/cover-preview`);
      toast.success("Preview verworpen");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwerpen mislukt");
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (busy) return;
    if (!window.confirm("AI cover verwijderen? Je kan later opnieuw genereren.")) return;
    setBusy(true);
    try {
      await api.delete(`/portfolios/${pid}/cover`);
      toast.success("AI cover verwijderd");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-neutral-200 bg-white p-4 space-y-3" data-testid="portfolio-cover-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600 inline-flex items-center gap-2">
            <Sparkles size={11} strokeWidth={1.75} /> AI cover
          </p>
          <p className="text-xs text-neutral-600 mt-1 max-w-md leading-relaxed">
            Genereer één magazine-style hero-afbeelding die de sfeer van je projecten
            samenvat. Wordt pas op de publieke pagina getoond als je hem <em>Bevestigt</em>.
            De kleur-palette volgt automatisch je gekozen PDF-template zodat cover en
            PDF-boekje visueel één geheel vormen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* When there's no preview pending, the committed cover can be
              refreshed or deleted directly. During a preview we hide
              these to avoid two "Ververs" buttons stealing attention. */}
          {!hasPreview && committedPath && (
            <button
              type="button"
              data-testid="portfolio-cover-delete"
              onClick={runDelete}
              disabled={busy}
              className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-rose-600 hover:text-rose-800 px-2 py-1 disabled:opacity-40"
            >
              Verwijderen
            </button>
          )}
          {!hasPreview && (
            <button
              type="button"
              data-testid="portfolio-cover-generate"
              onClick={runGenerate}
              disabled={busy || !canGenerate}
              className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-3 py-2 inline-flex items-center gap-1.5 ${
                busy
                  ? "border-neutral-300 text-neutral-400 cursor-wait"
                  : !canGenerate
                    ? "border-neutral-200 text-neutral-400 cursor-not-allowed"
                    : "border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white"
              }`}
              title={!canGenerate ? "Voeg eerst minstens één afbeelding toe aan een project" : "Genereer een AI cover preview"}
            >
              <Sparkles size={12} strokeWidth={1.75} />
              {busy ? "Genereren…" : committedPath ? "Vervang cover" : "Genereer cover"}
            </button>
          )}
        </div>
      </div>

      {/* Style hint field + preset chips. Disabled while committing so
          the user can't retype mid-flow. */}
      <div>
        <label
          htmlFor="portfolio-cover-style-hint"
          className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 block mb-1"
        >
          Extra styling (optioneel)
        </label>
        <input
          id="portfolio-cover-style-hint"
          type="text"
          data-testid="portfolio-cover-style-hint"
          value={styleHint}
          onChange={(e) => setStyleHint(e.target.value.slice(0, 200))}
          placeholder="bv. monochroom, bordeaux tinten, jaren '70 print, minimal…"
          maxLength={200}
          disabled={busy}
          className="w-full border border-neutral-300 focus:border-neutral-900 focus:outline-none px-3 py-2 text-sm bg-white"
        />
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="portfolio-cover-style-presets">
          {[
            "monochroom zwart-wit",
            "bordeaux + koperkleur",
            "aquarel pastel",
            "kraftpapier + inkt",
            "cyberpunk neon",
            "jaren '70 print",
          ].map((preset) => (
            <button
              key={preset}
              type="button"
              data-testid={`portfolio-cover-style-preset-${preset.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              onClick={() => setStyleHint(preset)}
              disabled={busy}
              className={`font-mono text-[0.55rem] uppercase tracking-[0.12em] px-2 py-1 border transition-colors ${
                styleHint === preset
                  ? "border-amber-600 bg-amber-600 text-white"
                  : "border-neutral-300 text-neutral-600 hover:border-amber-600 hover:text-amber-700"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {preset}
            </button>
          ))}
        </div>
        {styleHint.length > 0 && (
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-400 mt-1">
            {styleHint.length}/200 · wordt meegestuurd bij de volgende generatie
          </p>
        )}
      </div>

      {!canGenerate && (
        <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
          Voeg eerst een afbeelding toe aan minstens één project.
        </p>
      )}

      {/* PREVIEW PANE — de-emphasised amber border indicates unsaved state.
          Bevestig / Opnieuw / Verwerpen stack cleanly on both mobile and
          desktop. */}
      {hasPreview && previewSrc && (
        <div className="border-2 border-amber-500 bg-amber-50/60 p-3 space-y-3" data-testid="portfolio-cover-preview-pane">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-800 inline-flex items-center gap-1.5">
              <Sparkles size={11} strokeWidth={1.75} /> Preview — nog niet bevestigd
            </p>
            <p className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-500">
              Elke "Opnieuw" verbruikt credits
            </p>
          </div>
          <div className="border border-neutral-200 bg-white overflow-hidden">
            <img
              src={previewSrc}
              alt="AI cover preview"
              className="w-full h-48 sm:h-64 object-cover"
              data-testid="portfolio-cover-preview-image"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="portfolio-cover-commit"
              onClick={runCommit}
              disabled={busy}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-amber-700 bg-amber-700 hover:bg-amber-800 text-white px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-wait"
            >
              Bevestig
            </button>
            <button
              type="button"
              data-testid="portfolio-cover-regenerate"
              onClick={runGenerate}
              disabled={busy || !canGenerate}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-wait"
            >
              <Sparkles size={11} strokeWidth={1.75} />
              Opnieuw
            </button>
            <button
              type="button"
              data-testid="portfolio-cover-discard"
              onClick={runDiscardPreview}
              disabled={busy}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 px-2 py-2 disabled:opacity-40"
            >
              Verwerpen
            </button>
          </div>
        </div>
      )}

      {/* Committed cover preview only when there's no pending preview,
          so we don't confuse "current" with "proposed". */}
      {!hasPreview && committedSrc && (
        <div className="border border-neutral-200 bg-neutral-100 overflow-hidden" data-testid="portfolio-cover-preview">
          <img
            src={committedSrc}
            alt="AI cover"
            className="w-full h-48 sm:h-64 object-cover"
          />
        </div>
      )}

      {busy && <ThinkingLoader stage="match" testId="portfolio-cover-thinking" />}
    </div>
  );
}
